import { supabase } from '../supabase';
import { OnboardingData } from '../../types/onboarding';
import { insertUserSports } from './sports';
import { validateInvite } from './invites';

export async function resetPassword(email: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      console.error('Password reset error:', error);
      return { success: false, error: error.message };
    }
    return { success: true };
  } catch (error) {
    console.error('Password reset error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to send password reset email',
    };
  }
}

export async function signUpWithEmail(
  email: string,
  password: string,
  userData: OnboardingData,
  inviterId?: string,
  inviteCode?: string | null
) {
  console.log('🔍 Starting signup process for:', email);

  try {
    if (!userData.fullName || !userData.email || !userData.city) {
      throw new Error('Missing required fields: fullName, email, and city are mandatory.');
    }

    let confirmedUser: any = null;

    // ─── Invite Code Flow ──────────────────────────────────────────
    if (inviteCode) {
      console.log('ℹ️ Using invite code flow for signup');
      const inviteData = await validateInvite(inviteCode);
      if (!inviteData?.valid) throw new Error('Invalid or expired invite code');

      // signUp
      console.log('🔄 Creating user account with email:', email);
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { display_name: userData.fullName },
          emailRedirectTo: window.location.origin,
        },
      });
      if (signUpError) {
        console.error('❌ Signup error:', signUpError);
        throw signUpError;
      }
      // ← set session from signUp if present
      if (signUpData.session) {
        console.log('🔄 Setting initial session from signUp');
        await supabase.auth.setSession({
          access_token:  signUpData.session.access_token,
          refresh_token: signUpData.session.refresh_token,
        });
      }
      if (!signUpData.user) throw new Error('Failed to create user account');
      confirmedUser = signUpData.user;
      console.log('✅ User created:', confirmedUser.id);

      // immediate signIn
      console.log('🔄 Performing immediate sign in');
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (signInError) {
        console.error('❌ Sign in error after signup:', signInError);
        throw signInError;
      }
      // ← set session from signIn
      if (signInData.session) {
        console.log('🔄 Setting session from signIn');
        await supabase.auth.setSession({
          access_token:  signInData.session.access_token,
          refresh_token: signInData.session.refresh_token,
        });
      }
      console.log('✅ User created and signed in successfully');

    // ─── Magic-Link / Email-Confirm Flow ────────────────────────────
    } else {
      // check if already confirmed
      console.log('🔄 Checking if user already exists');
      const { data: getUserData, error: getUserError } = await supabase.auth.getUser();
      if (getUserError) {
        console.error('❌ Error getting user:', getUserError);
        throw getUserError;
      }

      if (getUserData?.user) {
        confirmedUser = getUserData.user;
        console.log('ℹ️ User exists. Updating password...');
        const { data: updateData, error: updateError } = await supabase.auth.updateUser({ password });
        if (updateError) {
          console.error('❌ Error updating password:', updateError);
          throw updateError;
        }
        confirmedUser = updateData.user ?? confirmedUser;
      } else {
        // initial signUp
        console.log('🔄 Creating new user account');
        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { display_name: userData.fullName },
            emailRedirectTo: window.location.origin,
          },
        });
        if (signUpError) {
          console.error('❌ Signup error:', signUpError);
          throw signUpError;
        }
        // ← set session from signUp if present
        if (signUpData.session) {
          console.log('🔄 Setting initial session from signUp');
          await supabase.auth.setSession({
            access_token:  signUpData.session.access_token,
            refresh_token: signUpData.session.refresh_token,
          });
        }
        // wait for confirmation
        console.log('🔄 Waiting for email confirmation...');
        for (let i = 0; i < 10; i++) {
          const { data: check, error: userError } = await supabase.auth.getUser();
          if (userError) {
            console.error('❌ Error checking user status:', userError);
            throw userError;
          }
          if (check.user?.email_confirmed_at) {
            confirmedUser = check.user;
            break;
          }
          await new Promise(r => setTimeout(r, 3000));
        }
        if (!confirmedUser) {
          console.error('❌ Email confirmation timeout.');
          throw new Error('Email not confirmed within expected time.');
        }
      }

      console.log('✅ Email confirmed:', confirmedUser);

      // signInWithPassword after confirmation
      console.log('🔄 Signing in after confirmation');
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (signInError) {
        console.error('❌ Immediate sign-in failed:', signInError);
        throw signInError;
      }
      // ← set session from signIn
      if (signInData.session) {
        console.log('🔄 Setting session from signIn');
        await supabase.auth.setSession({
          access_token:  signInData.session.access_token,
          refresh_token: signInData.session.refresh_token,
        });
      }
      console.log('✅ Immediate sign-in succeeded:', signInData.user);
    }

   // ─── Upsert Profile ─────────────────────────────────────────────
console.log('🔄 Creating user profile record');

// Wait a moment to ensure auth is fully established
await new Promise(resolve => setTimeout(resolve, 1000));

// Refresh session and get user again to ensure we have the latest data
await supabase.auth.refreshSession();
const { data: refreshedUserData } = await supabase.auth.getUser();
const userId = refreshedUserData.user?.id || confirmedUser.id;

console.log('🔄 Creating profile for user:', userId);

const { data: profileData, error: profileError } = await supabase
  .from('users')
  .upsert(
    {
      auth_id:       userId,
      full_name:     userData.fullName,
      city:          userData.city,
      company:       userData.company,
      job_type:      userData.jobTitle,
      gender:        userData.gender,
      skill_level:   'beginner',
      facebook_url:  userData.facebookUrl || null,
      linkedin_url:  userData.linkedinUrl   || null,
      instagram_url: userData.instagramUrl  || null,
    },
    { onConflict: 'auth_id' }
  )
  .select()
  .single();

if (profileError) {
  console.error('❌ Failed to create user profile:', profileError);
  throw new Error('Failed to create user profile');
}

console.log('✅ User profile created:', profileData);


    // ─── Insert Club Memberships ─────────────────────────────────────
    if (userData.selectedClubs?.length) {
      console.log('🔄 Adding club memberships');
      const clubMemberships = userData.selectedClubs.map(c => ({
        user_id:    profileData.id,
        club_id:    c.club_id,
        is_primary: c.is_primary,
      }));
      const { error: clubError } = await supabase.from('user_clubs').insert(clubMemberships);
      if (clubError) {
        console.error('❌ Error adding club memberships:', clubError);
      } else {
        console.log('✅ Club memberships added successfully');
        const primary = userData.selectedClubs.find(c => c.is_primary);
        if (primary) {
          const { error: upd } = await supabase
            .from('users')
            .update({ club_id: primary.club_id })
            .eq('id', profileData.id);
          if (upd) console.error('❌ Error updating primary club:', upd);
        }
      }
    }

    // ─── Invite, Connections, Sports ───────────────────────────────
    if (inviteCode) {
      console.log('🔄 Processing invite code acceptance');
      const { error: inviteErr } = await supabase.rpc('accept_invite', {
        p_invite_code: inviteCode,
        p_invitee_email: email,
        p_phone_number: null,
        p_country_code: userData.phoneNumber?.substring(0, 2) || null,
      });
      if (inviteErr) console.error('❌ Error accepting invite:', inviteErr);
    }
    if (inviterId) {
      console.log('🔄 Creating connection with inviter');
      await supabase.from('connections').insert({
        initiator_id: inviterId,
        receiver_id:  profileData.id,
        status:       'accepted',
      });
      await supabase.rpc('get_or_create_conversation', {
        user1_id: inviterId,
        user2_id: profileData.id,
      });
    }
    // always connect Lucy AI
    console.log('🔄 Creating connection with Lucy AI');
    await supabase.from('connections').insert({
      initiator_id: 'a125a4ca-5ff3-45d6-b8b7-cfe3c015c600',
      receiver_id:  profileData.id,
      status:       'accepted',
    });
    await supabase.rpc('get_or_create_conversation', {
      user1_id: 'a125a4ca-5ff3-45d6-b8b7-cfe3c015c600',
      user2_id: profileData.id,
    });
    if (userData.sports?.length) {
      console.log('🔄 Adding user sports');
      await insertUserSports(confirmedUser.id, userData.sports);
    }

    return { user: confirmedUser };
  } catch (error) {
    console.error('❌ Error in signUpWithEmail:', error);
    throw error instanceof Error ? error : new Error('Unexpected error during signup');
  }
}

export async function signInWithEmail(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) {
    console.error('❌ Login error:', error);
    throw error;
  }
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError || !sessionData.session) {
    console.error('❌ Failed to retrieve session after login:', sessionError);
    throw new Error('Session not established after login');
  }
  localStorage.setItem('supabaseSession', JSON.stringify(sessionData.session));
  console.log('✅ Login successful, session active');
  return sessionData;
}

export async function performPasswordlessLogin() {
  const res = await fetch('/api/login/biometric', {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const msg = await res.text();
    console.error('Biometric login failed:', msg);
    throw new Error('Biometric login failed');
  }
  window.location.href = '/dashboard';
}